import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router";
import { Task } from "../../state/Task";
import { ChecklistItem } from "../../state/ChecklistItem";
import { LifePart } from "../../state/LifePart";
import { emptyChecklistItem, emptyTask } from "../../state/reducer";
import { deadlineColor } from "../../state/helpers";
import { useData } from "../../storage/accessors";
import { useLicense } from "../../hooks/useLicense";
import { useScreenSize } from "../../contexts/screenSizeContext";
import { useTaskModal } from "../TaskModalContext";
import Icon from "../Icon";
import {
    faPlus as add,
    faArrowRight as arrowForwardOutline,
    faCheckSquare as checkboxOutline,
    faList as listOutline,
    faSearch as search,
    faTrash as trashBin,
    faGripVertical as grip
} from '@fortawesome/free-solid-svg-icons';
import { format } from "date-fns";

type Props = {
    task: Task,
    fromDate?: Date,
    checklistItems: ChecklistItem[],
    part: LifePart,
    onOpenTask?: (taskUuid: string, fromDate?: Date) => void,
    onCloseModal?: () => void
};

const TaskChecklist: React.FC<Props> = ({ task, fromDate, checklistItems, part, onOpenTask, onCloseModal }) => {
    const { setTaskModal } = useTaskModal() || {};
    const { checklistItems: checklistItemsStore, tasks } = useData();
    const { getLimit } = useLicense();
    const navigate = useNavigate();
    const ref = useRef(null);
    const [filterTitle, setFilterTitle] = useState('');
    const [lastAddedItemUuid, setLastAddedItemUuid] = useState<string | null>(null);
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";

    // Скролл к последнему добавленному элементу
    useEffect(() => {
        if (lastAddedItemUuid && isMobile) {
            const element = document.getElementById(`checklist-item-${lastAddedItemUuid}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                const input = element.querySelector('input[type="text"]') as HTMLInputElement;
                if (input) {
                    input.focus();
                    input.select();
                }
            }
            setLastAddedItemUuid(null);
        }
    }, [lastAddedItemUuid, isMobile]);

    const addTaskOnEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (!checkChecklistsLimit(onCloseModal)) return;
            const newItem = emptyChecklistItem(task.uuid);
            newItem.order = checklistItems.length
            checklistItemsStore.create(newItem)
            setLastAddedItemUuid(newItem.uuid);

            // @ts-ignore
            setTimeout(() => ref.current?.focus(), 500);
        }
    }, [task.uuid, checklistItems.length, checklistItemsStore]);
    const checkTasksLimit = (onLimitReached?: () => void): boolean => {
        const tasksCount = Object.values(tasks.objects).length;
        const tasksLimit = getLimit('limit_tasks');

        if (tasksCount >= tasksLimit) {
            onLimitReached?.();
            navigate('/my/tariffs?reason=limit_tasks');
            return false;
        }
        return true;
    };
    const checkChecklistsLimit = (onLimitReached?: () => void): boolean => {
        const checklistsCount = Object.values(checklistItemsStore.objects).length;
        const checklistsLimit = getLimit('limit_checklists');

        if (checklistsCount >= checklistsLimit) {
            onLimitReached?.();
            navigate('/my/tariffs?reason=limit_checklists');
            return false;
        }
        return true;
    };
    const makeTask = (item: ChecklistItem) => {
        if (!checkTasksLimit(onCloseModal)) return;

        const newTask = emptyTask(task.lifepart, item.title || "", task.order ? task.order + 1 : task.order);
        newTask.parentTask = task.uuid;
        newTask.parentChecklistItem = item.uuid;
        tasks.create(newTask);
        checklistItemsStore.save(item.uuid, { innerTask: newTask.uuid });
        if (setTaskModal) {
            setTaskModal({ taskUUID: newTask.uuid, fromDate });
        } else if (onOpenTask) {
            onOpenTask(newTask.uuid, fromDate);
        }

    }
    const itemDelete = (item: ChecklistItem) => {
        if (item.innerTask) {
            tasks.delete(item.innerTask);
        }
        checklistItemsStore.delete(item.uuid);
    }

    const filteredItems = checklistItems
        .filter(f => (task.hideDoneTasks ? !f.isDone : true))
        .filter(f => (filterTitle.length > 0 ? f.title === "" || f.title.toLowerCase().lastIndexOf(filterTitle.toLocaleLowerCase()) >= 0 : 1));

    // ===== Сортировка чеклистов перетаскиванием =====
    const [draggedUuid, setDraggedUuid] = useState<string | null>(null);
    const [dropTarget, setDropTarget] = useState<{ uuid: string; position: "before" | "after" } | null>(null);
    // Touch-drag (мобильные): native HTML5 DnD там не работает
    const touchDragRef = useRef<string | null>(null);
    const touchOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const [touchDragItem, setTouchDragItem] = useState<ChecklistItem | null>(null);
    const [touchDragPos, setTouchDragPos] = useState<{ x: number; y: number } | null>(null);

    // Переставляет элемент before/after целевого.
    // Вместо перенумерации всего списка берём order между соседями вставки
    // (срединное значение) — один перенос = ровно один save. Иначе пачка
    // сохранений порождает пачку эхо-сообщений от сервера, и
    // react-use-websocket (flushSync на каждое входящее сообщение) падает
    // с "Maximum update depth exceeded"
    const applyReorder = useCallback((fromUuid: string, targetUuid: string, position: "before" | "after") => {
        if (fromUuid === targetUuid) return;
        const ordered = [...checklistItems];
        const fromIndex = ordered.findIndex(i => i.uuid === fromUuid);
        const targetIndex = ordered.findIndex(i => i.uuid === targetUuid);
        if (fromIndex === -1 || targetIndex === -1) return;

        const target = ordered[targetIndex];
        const targetOrder = target.order ?? 0;

        let newOrder: number;
        if (position === "before") {
            const prev = targetIndex > 0 ? ordered[targetIndex - 1] : null;
            const prevOrder = prev && prev.uuid !== fromUuid ? (prev.order ?? 0) : null;
            newOrder = prevOrder !== null ? (prevOrder + targetOrder) / 2 : targetOrder - 1;
        } else {
            const next = targetIndex < ordered.length - 1 ? ordered[targetIndex + 1] : null;
            const nextOrder = next && next.uuid !== fromUuid ? (next.order ?? 0) : null;
            newOrder = nextOrder !== null ? (targetOrder + nextOrder) / 2 : targetOrder + 1;
        }

        checklistItemsStore.save(fromUuid, { order: newOrder });
    }, [checklistItems, checklistItemsStore]);

    const resetDragState = useCallback(() => {
        setDraggedUuid(null);
        setDropTarget(null);
        setTouchDragItem(null);
        setTouchDragPos(null);
        setDraggableRow(null);
        touchDragRef.current = null;
    }, []);

    // --- Desktop: HTML5 drag&drop, старт только за grip-зону ---
    // draggable включается только у строки, за grip которой зажата мышь,
    // чтобы не мешать выделению текста в инпутах внутри строки
    const [draggableRow, setDraggableRow] = useState<string | null>(null);

    const handleRowDragStart = (e: React.DragEvent, item: ChecklistItem) => {
        setDraggedUuid(item.uuid);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", item.uuid);
    };

    const handleRowDragOver = (e: React.DragEvent, item: ChecklistItem) => {
        if (!draggedUuid || draggedUuid === item.uuid) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const position: "before" | "after" = e.clientY - rect.top < rect.height / 2 ? "before" : "after";
        if (dropTarget?.uuid !== item.uuid || dropTarget.position !== position) {
            setDropTarget({ uuid: item.uuid, position });
        }
    };

    const handleRowDrop = (e: React.DragEvent, item: ChecklistItem) => {
        e.preventDefault();
        const fromUuid = e.dataTransfer.getData("text/plain") || draggedUuid;
        if (fromUuid && dropTarget) {
            applyReorder(fromUuid, dropTarget.uuid, dropTarget.position);
        }
        resetDragState();
    };

    // --- Mobile: touch-перетаскивание за grip-зону ---
    const handleGripTouchStart = (e: React.TouchEvent, item: ChecklistItem) => {
        touchDragRef.current = item.uuid;
        setTouchDragItem(item);
        const touch = e.touches[0];
        const row = (e.target as HTMLElement).closest("[data-checklist-row]") as HTMLElement | null;
        if (row) {
            const rect = row.getBoundingClientRect();
            touchOffsetRef.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
        } else {
            touchOffsetRef.current = { x: 0, y: 0 };
        }
        setTouchDragPos({ x: touch.clientX, y: touch.clientY });
    };

    const handleListTouchMove = (e: React.TouchEvent) => {
        if (!touchDragRef.current) return;
        const touch = e.touches[0];
        setTouchDragPos({ x: touch.clientX, y: touch.clientY });

        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        const row = elementBelow?.closest("[data-checklist-uuid]") as HTMLElement | null;
        const targetUuid = row?.getAttribute("data-checklist-uuid");

        if (targetUuid && targetUuid !== touchDragRef.current) {
            const rect = row!.getBoundingClientRect();
            setDropTarget({
                uuid: targetUuid,
                position: touch.clientY - rect.top < rect.height / 2 ? "before" : "after",
            });
        } else {
            setDropTarget(null);
        }

        // Автопрокрутка тела модалки, когда тянем у её краёв
        const scroller = (e.currentTarget as HTMLElement).closest(".modal-body") as HTMLElement | null;
        if (scroller) {
            const rect = scroller.getBoundingClientRect();
            const threshold = 60;
            if (touch.clientY < rect.top + threshold) scroller.scrollTop -= 12;
            else if (touch.clientY > rect.bottom - threshold) scroller.scrollTop += 12;
        }
    };

    const handleListTouchEnd = (e: React.TouchEvent) => {
        const fromUuid = touchDragRef.current;
        if (!fromUuid) return;
        if (dropTarget) {
            applyReorder(fromUuid, dropTarget.uuid, dropTarget.position);
        }
        // Предотвращаем "клик" по элементу сразу после перестановки
        e.preventDefault();
        resetDragState();
    };

    const dropPosition = (uuid: string): "before" | "after" | null =>
        dropTarget?.uuid === uuid && (draggedUuid || touchDragItem) ? dropTarget.position : null;

    // Подсветка места вставки. В таблице box-shadow на <tr> браузеры не рисуют
    // при border-collapse, поэтому стиль применяется и к строке, и к её ячейкам
    const dropIndicator = (uuid: string): React.CSSProperties | undefined => {
        const position = dropPosition(uuid);
        if (!position) return undefined;
        return {
            boxShadow: position === "before"
                ? "inset 0 3px 0 0 var(--bs-primary)"
                : "inset 0 -3px 0 0 var(--bs-primary)",
            backgroundColor: "rgba(13, 110, 253, 0.06)",
        };
    };

    return <>
        {checklistItems.length > 4 && <Row className="mt-4 mb-2 ">
            <Col>
                <Form.Group>
                    <Form.Switch id={`filterDone`} type="switch" checked={!!task.hideDoneTasks} onChange={
                        (e: any) => {
                            tasks.save(task.uuid, { hideDoneTasks: e.target.checked })
                        }
                    } />
                    <Form.Label htmlFor="filterDone">Скрыть выполенные</Form.Label>
                </Form.Group>
            </Col>
            <Col>
                <Form.Group>
                    <InputGroup>
                        <InputGroup.Text>
                            <Icon icon={search} />
                        </InputGroup.Text>
                        <Form.Control type="text" value={filterTitle} onChange={(e: any) => setFilterTitle(e.target.value)} placeholder="поиск" />
                    </InputGroup>

                </Form.Group>
            </Col>
        </Row>}

        {/* Desktop View: Table */}
        {!isMobile && (
            <><Table>
                <thead>
                    <tr>
                        <th style={{ width: 32 }}></th>
                        <th>Готов</th>
                        <th>Описание</th>
                        <th></th>
                        <th>Оценка</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map(item => {
                        let deadlineClass = "";
                        if (!item.isDone)
                            deadlineClass = deadlineColor(item.deadline);
                        if (deadlineClass) {
                            deadlineClass = `text-bd-${deadlineClass}`
                        }
                        const cellStyle = dropIndicator(item.uuid);
                        return <tr
                            key={item.uuid}
                            data-checklist-row
                            data-checklist-uuid={item.uuid}
                            draggable={draggableRow === item.uuid}
                            onDragStart={(e) => handleRowDragStart(e, item)}
                            onDragOver={(e) => handleRowDragOver(e, item)}
                            onDrop={(e) => handleRowDrop(e, item)}
                            onDragEnd={resetDragState}
                        >
                            <td
                                className="text-muted"
                                title="Перетащите, чтобы изменить порядок"
                                style={{ cursor: "grab", userSelect: "none", ...cellStyle }}
                                onMouseDown={() => setDraggableRow(item.uuid)}
                                onMouseUp={() => setDraggableRow(null)}
                            >
                                <Icon icon={grip} />
                            </td>
                            <td width={80} className={deadlineClass} style={cellStyle}>
                                <Form.Check disabled={Boolean(item.innerTask)} type="checkbox" checked={item.isDone} onChange={(e: any) => checklistItemsStore.save(item.uuid, { isDone: e.target.checked, dateDone: e.target.checked ? format(new Date(), 'yyyy-MM-dd') : '' })} />
                            </td>
                            <td className={deadlineClass} style={cellStyle}>
                                <Form.Control ref={ref} onKeyUp={addTaskOnEnter} size="sm" isValid={item.isDone} disabled={item.isDone || Boolean(item.innerTask)} style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined} defaultValue={item.title} onBlur={(e: any) => checklistItemsStore.save(item.uuid, { title: e.target.value })} />
                            </td>
                            <td className={deadlineClass} style={cellStyle}>
                                {!item.isDone && !item.innerTask && <Button title="Создать отдельную задачу" disabled={item.isDone || Boolean(item.innerTask)} size="sm" variant="primary" onClick={() => makeTask(item)}><Icon icon={checkboxOutline} /><Icon icon={arrowForwardOutline} /><Icon icon={listOutline} /></Button>}
                                {item.innerTask && <Button title="Перейти к карточке" size="sm" variant="primary" onClick={() => {
                                    if (item.innerTask) {
                                        if (setTaskModal) {
                                            setTaskModal({ taskUUID: item.innerTask, fromDate });
                                        } else if (onOpenTask) {
                                            onOpenTask(item.innerTask, fromDate);
                                        }
                                    }
                                }}><Icon icon={listOutline} /></Button>}
                            </td>
                            <td className={deadlineClass} style={cellStyle}>
                                <Form.Control type="time" onKeyUp={addTaskOnEnter} size="sm" isValid={item.isDone} disabled={item.isDone || Boolean(item.innerTask)} style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined} defaultValue={item.needTime || ""} onBlur={(e: any) => checklistItemsStore.save(item.uuid, { needTime: e.target.value })} />
                            </td>
                            <td width={100} className={deadlineClass} style={cellStyle}>
                                <Button size="sm" variant="danger" onClick={() => itemDelete(item)}><Icon icon={trashBin} /></Button>
                            </td>
                        </tr>
                    })}

                </tbody>
            </Table>
                <Button variant="success" size="sm" onClick={() => {
                    if (!checkChecklistsLimit(onCloseModal)) return;
                    const newItem = emptyChecklistItem(task.uuid);
                    newItem.order = Math.max(...checklistItems.map(cl => cl.order || 0), 0) + 1;
                    checklistItemsStore.create(newItem);
                    // @ts-ignore
                    setTimeout(() => ref.current?.focus(), 50);
                }}>
                    <Icon icon={add}></Icon>
                </Button></>
        )}

        {/* Mobile View: List */}
        {isMobile && (
            <ul
                className="list-group list-group-flush"
                onTouchMove={handleListTouchMove}
                onTouchEnd={handleListTouchEnd}
                onTouchCancel={resetDragState}
            >
                {filteredItems.map(item => {
                    let deadlineClass = "";
                    if (!item.isDone)
                        deadlineClass = deadlineColor(item.deadline);
                    if (deadlineClass) {
                        deadlineClass = `text-bd-${deadlineClass}`
                    }
                    return <li
                        key={item.uuid}
                        id={`checklist-item-${item.uuid}`}
                        data-checklist-row
                        data-checklist-uuid={item.uuid}
                        style={dropIndicator(item.uuid)}
                        className={`list-group-item px-2 py-2 ${deadlineClass}`}
                    >
                        {/* Левая колонка (перетаскивание + чекбокс) фиксирована,
                            правая содержит название и действия, поэтому вторая строка
                            начинается ровно под инпутом названия */}
                        <div className="d-flex gap-2">
                            <div className="d-flex align-items-center flex-shrink-0">
                                <span
                                    className="text-muted"
                                    title="Перетащите, чтобы изменить порядок"
                                    style={{ cursor: "grab", touchAction: "none", userSelect: "none", padding: "4px 2px" }}
                                    onTouchStart={(e) => handleGripTouchStart(e, item)}
                                >
                                    <Icon icon={grip} />
                                </span>
                                <input
                                    type="checkbox"
                                    className="form-check-input m-0 ms-1"
                                    disabled={Boolean(item.innerTask)}
                                    checked={item.isDone}
                                    onChange={(e: any) => checklistItemsStore.save(item.uuid, { isDone: e.target.checked, dateDone: e.target.checked ? format(new Date(), 'yyyy-MM-dd') : '' })}
                                />
                            </div>
                            <div className="d-flex flex-column gap-1 flex-grow-1" style={{ minWidth: 0 }}>
                                <input
                                    ref={ref}
                                    onKeyUp={addTaskOnEnter}
                                    className={`form-control form-control-sm ${item.isDone ? 'is-valid' : ''}`}
                                    disabled={item.isDone || Boolean(item.innerTask)}
                                    style={{ minWidth: 0, ...(item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined) }}
                                    defaultValue={item.title}
                                    onBlur={(e: any) => checklistItemsStore.save(item.uuid, { title: e.target.value })}
                                    placeholder="Описание"
                                />
                                <div className="d-flex align-items-center gap-1">
                                    {!item.isDone && !item.innerTask && <Button title="Создать отдельную задачу" disabled={item.isDone || Boolean(item.innerTask)} size="sm" variant="primary" onClick={() => makeTask(item)}><Icon icon={checkboxOutline} /><Icon icon={arrowForwardOutline} /><Icon icon={listOutline} /></Button>}
                                    {item.innerTask && <Button title="Перейти к карточке" size="sm" variant="primary" onClick={() => {
                                        if (item.innerTask) {
                                            if (setTaskModal) {
                                                setTaskModal({ taskUUID: item.innerTask, fromDate });
                                            } else if (onOpenTask) {
                                                onOpenTask(item.innerTask, fromDate);
                                            }
                                        }
                                    }}><Icon icon={listOutline} /></Button>}
                                    <Button size="sm" variant="danger" onClick={() => itemDelete(item)}><Icon icon={trashBin} /></Button>
                                    <input
                                        type="time"
                                        onKeyUp={addTaskOnEnter}
                                        className={`form-control form-control-sm ms-auto ${item.isDone ? 'is-valid' : ''}`}
                                        disabled={item.isDone || Boolean(item.innerTask)}
                                        style={{ width: "auto", ...(item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined) }}
                                        defaultValue={item.needTime || ""}
                                        onBlur={(e: any) => checklistItemsStore.save(item.uuid, { needTime: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </li>
                })}
                <li className="list-group-item text-center py-2">
                    <Button variant="success" size="sm" onClick={() => {
                        if (!checkChecklistsLimit(onCloseModal)) return;
                        const newItem = emptyChecklistItem(task.uuid);
                        newItem.order = Math.max(...checklistItems.map(cl => cl.order || 0), 0) + 1;
                        checklistItemsStore.create(newItem);
                        setLastAddedItemUuid(newItem.uuid);
                    }}>
                        <Icon icon={add}></Icon>
                    </Button>
                </li>
            </ul>
        )}

        {/* Превью перетаскиваемого элемента (мобильные) */}
        {touchDragItem && touchDragPos && (
            <div
                style={{
                    position: "fixed",
                    left: touchDragPos.x - touchOffsetRef.current.x,
                    top: touchDragPos.y - touchOffsetRef.current.y,
                    minWidth: 200,
                    maxWidth: "90vw",
                    zIndex: 9999,
                    pointerEvents: "none",
                    opacity: 0.9,
                    transform: "rotate(2deg)",
                }}
            >
                <div className="d-flex align-items-center gap-2 p-2 border rounded bg-body shadow">
                    <span className="text-muted"><Icon icon={grip} /></span>
                    <span className="text-truncate">{touchDragItem.title || "Без названия"}</span>
                </div>
            </div>
        )}
    </>
}

export default TaskChecklist;
